import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  weeklyReportApi, type InternalWeeklyReport, type WeeklyUploadFile, type WeeklyUploadKind, type WeeklyUploadWeek,
} from '@/lib/apiClient';
import { Button, Modal } from '@/components';
import { GraphConsentError, uploadToJobFolder, type UploadResult } from '@/lib/graphUpload';
import { statusOf } from '@/lib/httpErrors';
import { carrySummary, filedSummary, NotAWordFileError, readFinalizedReport, type ReadReport } from './weeklyReportRead';
import { chainStartWarning, reportFilename, theWeekOf, weekOf } from './weeklyReportModel';

/**
 * The selected week's finalized reports (#576 follow-up): what has been
 * uploaded, and, for editors, the uploads.
 *
 * After exporting, filling in and sending a week's reports, the PM uploads the
 * finished files here. They go to the job's SharePoint folder ("Weekly
 * Reports"); the internal one's whole text shows on the tab for its week
 * ("After upload"), and what was typed in them starts next week's.
 *
 * ⚠️ IN ORDER. The server refuses a week until the week before it is done
 * (internal copy uploaded, or marked "No report"); this panel says so up front
 * and links to the week owed. The client copy is optional and never blocks.
 */

export const REPORTS_SUBFOLDER = 'Weekly Reports';
/** Larger than any report with a week of photos; past this, something else was picked. */
export const MAX_UPLOAD_BYTES = 100 * 1024 * 1024;

const css = `
  .wr-final { margin-bottom: var(--ew-s-4); padding: 12px 16px; background: var(--ew-white); border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-md); font: var(--ew-fw-regular) var(--ew-fs-body-sm)/1.45 var(--ew-font); color: var(--ew-dark); }
  .wr-final-hd { display: flex; align-items: center; gap: var(--ew-s-2); flex-wrap: wrap; margin-bottom: 8px; font-weight: 700; }
  .wr-final-flag { margin: 0 0 10px; padding: 8px 12px; border-radius: var(--ew-r-sm); background: var(--ew-safety-50); border: 1px solid var(--ew-safety-100); color: var(--ew-safety-700); font-weight: 600; }
  .wr-final-row { display: flex; gap: var(--ew-s-2); flex-wrap: wrap; padding: 3px 0; }
  .wr-final-label { width: 64px; flex: 0 0 auto; font-weight: 600; }
  .wr-final-row a, .wr-final-row a:visited { color: var(--ew-green-700); }
  .wr-final-actions { display: flex; gap: var(--ew-s-2); flex-wrap: wrap; align-items: center; margin-top: 10px; }
  .wr-final-note { margin-top: 8px; color: var(--ew-n-500); }
  .wr-final-ok { margin-top: 8px; color: var(--ew-green-700); font-weight: 600; }
  .wr-final-err { margin-top: 8px; padding: 8px 12px; border-radius: var(--ew-r-sm); background: var(--ew-danger-50); color: var(--ew-danger-700); }
  .wr-up-list { margin: 6px 0 0; padding-left: 18px; }
  .wr-up-warn { margin-top: 10px; padding: 8px 12px; border-radius: var(--ew-r-sm); background: var(--ew-safety-50); color: var(--ew-safety-700); }
  .wr-up-check { display: flex; gap: 8px; align-items: flex-start; margin-top: 8px; }
  .wr-up-check input { margin-top: 3px; }
  .wr-up-reason { width: 100%; min-height: 80px; box-sizing: border-box; padding: 8px 10px; border: 1px solid var(--ew-n-200); border-radius: var(--ew-r-sm); font: var(--ew-fw-regular) var(--ew-fs-body-sm) var(--ew-font); }
`;

const KIND_LABEL: Record<WeeklyUploadKind, string> = { internal: 'Internal', client: 'Client' };

/** "September 28, 2026", from the upload's UTC time, in Eastern. */
export function uploadedOn(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-US', { timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric' });
}

/**
 * Second-guesses worth a confirmation before a file is taken as `kind`'s
 * report for `weekStart`: the wrong copy, or the wrong week. For an upload,
 * and for reading an uploaded file again, which may have been replaced since.
 */
export function fileWarnings(read: Pick<ReadReport, 'kind' | 'weekStart'>, kind: WeeklyUploadKind, weekStart: string): string[] {
  return [
    read.kind && read.kind !== kind
      ? `This looks like the ${read.kind} copy (its footer says so), not the ${kind} copy.`
      : null,
    read.weekStart && read.weekStart !== weekStart
      ? `Its look-ahead starts on ${theWeekOf(read.weekStart)}, so it may be another week’s report.`
      : null,
  ].filter((w): w is string => !!w);
}

function FileLine({ file }: { file: WeeklyUploadFile }) {
  const on = uploadedOn(file.uploadedAt);
  return (
    <span>
      Uploaded{on ? ` ${on}` : ''}{file.uploadedBy ? ` by ${file.uploadedBy}` : ''} ·{' '}
      <a href={file.webUrl} target="_blank" rel="noopener noreferrer">Open in SharePoint</a>
      {file.versions > 1 && <span className="wr-muted"> · {file.versions} versions, newest shown</span>}
    </span>
  );
}

export default function FinalizedReports({
  projectId, report, week, chainStart, thisWeek, available, canEdit, locked, jobFolderUrl, getToken, onGoToWeek,
}: {
  projectId: number;
  /** The report on screen: names the uploaded file the way the export does. */
  report: InternalWeeklyReport;
  /** The selected week's uploads; undefined while they load. */
  week: WeeklyUploadWeek | undefined;
  /** The job's first filed week, where its finalized reports start. */
  chainStart: string | null;
  /** The current week (Eastern), as the server reckons it. */
  thisWeek: string;
  /** False until the uploads table exists. */
  available: boolean;
  canEdit: boolean;
  /** The job has no Sage job number yet, so nothing can be filed on it. */
  locked: boolean;
  jobFolderUrl: string | null;
  /** The SharePoint token, taken inside the click (it may open the sign-in popup). */
  getToken: () => Promise<string>;
  onGoToWeek: (weekStart: string) => void;
}) {
  const [uploading, setUploading] = useState<WeeklyUploadKind | null>(null);
  const [markingNoReport, setMarkingNoReport] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const weekStart = report.window.weekStart;

  if (!available) {
    // Only for the people who would upload: a viewer has nothing to do about it.
    return canEdit ? (
      <div className="wr-final">
        <style>{css}</style>
        <div className="wr-final-hd">Finalized reports</div>
        <div className="wr-final-note" style={{ marginTop: 0 }}>Uploading finalized reports isn&rsquo;t set up yet.</div>
      </div>
    ) : null;
  }
  if (!week) return null;

  const blockedBy = week.closedReason === 'previous-missing' ? week.blockedBy : null;
  // Only an internal upload or a "No report" mark starts the chain; a client copy doesn't.
  const startWarning = chainStartWarning(weekStart, thisWeek, chainStart);
  return (
    <div className="wr-final" data-testid="finalized-reports">
      <style>{css}</style>
      <div className="wr-final-hd">Finalized reports · {weekOf(weekStart)}</div>

      {/* Names the week: on a past week, "this week's report" would read as the current one.
          Not on a job that can't file yet: nobody could clear it. */}
      {week.flagged && !locked && (
        <div className="wr-final-flag" role="status">
          ⚑ The finalized internal report for {theWeekOf(weekStart)} hasn&rsquo;t been uploaded yet.
          {canEdit ? ' Upload it once it has been sent, or mark the week "No report".' : ''}
        </div>
      )}

      <div className="wr-final-row">
        <span className="wr-final-label">Internal</span>
        {week.internal
          ? <FileLine file={week.internal} />
          : week.noReport
            ? <span>No report: {week.noReport.reason}{week.noReport.by ? <span className="wr-muted"> (marked by {week.noReport.by})</span> : null}</span>
            : <span className="wr-muted">Not uploaded</span>}
      </div>
      <div className="wr-final-row">
        <span className="wr-final-label">Client</span>
        {week.client ? <FileLine file={week.client} /> : <span className="wr-muted">Not uploaded (only in weeks one was sent)</span>}
      </div>

      {canEdit && (
        locked ? (
          <div className="wr-final-note">Reports can be filed once the job has a Sage job number.</div>
        ) : week.closedReason === 'future' ? (
          <div className="wr-final-note">Reports for {theWeekOf(weekStart)} can be uploaded once that week starts.</div>
        ) : week.closedReason === 'before-start' ? (
          <div className="wr-final-note">
            Finalized reports on this job start with {chainStart ? theWeekOf(chainStart) : 'its first upload'}; earlier weeks can&rsquo;t be added.
          </div>
        ) : blockedBy ? (
          <div className="wr-final-note">
            Upload the finalized internal report for {theWeekOf(blockedBy)} first, or mark that week &ldquo;No report&rdquo;: weeks are uploaded in order.{' '}
            <button type="button" className="ew-btn-link" onClick={() => onGoToWeek(blockedBy)}>Go to that week</button>
          </div>
        ) : week.open ? (
          <div className="wr-final-actions">
            {jobFolderUrl ? (
              <>
                <Button size="sm" variant="secondary" onClick={() => { setNotice(null); setUploading('internal'); }}>
                  {week.internal ? 'Upload a corrected internal' : 'Upload internal'}
                </Button>
                <Button size="sm" variant="secondary" onClick={() => { setNotice(null); setUploading('client'); }}>
                  {week.client ? 'Upload a corrected client' : 'Upload client'}
                </Button>
              </>
            ) : (
              <span className="wr-final-note" style={{ marginTop: 0 }}>
                Link the job&rsquo;s SharePoint Job Folder on the Documents tab to upload reports here.
              </span>
            )}
            {/* "No report", not "No report this week": on a past week that reads as the current one. */}
            {!week.internal && !week.noReport && (
              <button type="button" className="ew-btn-link" onClick={() => { setNotice(null); setMarkingNoReport(true); }}>Mark &ldquo;No report&rdquo;</button>
            )}
          </div>
        ) : null
      )}
      {notice && <div className="wr-final-ok" role="status">{notice}</div>}

      {uploading && jobFolderUrl && (
        <UploadModal
          projectId={projectId}
          report={report}
          kind={uploading}
          startWarning={uploading === 'internal' ? startWarning : null}
          jobFolderUrl={jobFolderUrl}
          getToken={getToken}
          onClose={() => setUploading(null)}
          onDone={(msg) => { setUploading(null); setNotice(msg); }}
        />
      )}
      {markingNoReport && (
        <NoReportModal
          projectId={projectId}
          weekStart={weekStart}
          startWarning={startWarning}
          onClose={() => setMarkingNoReport(false)}
          onDone={(msg) => { setMarkingNoReport(false); setNotice(msg); }}
        />
      )}
    </div>
  );
}

// ─── Upload one finalized file ───────────────────────────────────────────────

function UploadModal({ projectId, report, kind, startWarning, jobFolderUrl, getToken, onClose, onDone }: {
  projectId: number;
  report: InternalWeeklyReport;
  kind: WeeklyUploadKind;
  /** Set when this upload would start the job's chain on an old week: see chainStartWarning. */
  startWarning: string | null;
  jobFolderUrl: string;
  getToken: () => Promise<string>;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const qc = useQueryClient();
  const weekStart = report.window.weekStart;
  const [file, setFile] = useState<File | null>(null);
  const [read, setRead] = useState<ReadReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [anyway, setAnyway] = useState(false);
  const [startOk, setStartOk] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  /** Kept once the file is in SharePoint, so a retry records it rather than uploading a second copy. */
  const [uploaded, setUploaded] = useState<UploadResult | null>(null);
  const busyRef = useRef(false);
  /**
   * ⚠️ Which pick is current. Reading a file takes a moment; pick another in
   * the meantime and the first read must not land over it, or the window shows
   * one file's sections and records them against the other.
   */
  const pickRef = useRef(0);

  const choose = async (f: File | undefined) => {
    const pick = ++pickRef.current;
    setFile(f ?? null);
    setRead(null);
    setError(null);
    setAnyway(false);
    setUploaded(null);
    if (!f) return;
    if (f.size > MAX_UPLOAD_BYTES) { setError('That file is over 100 MB. Pick the report’s .docx.'); return; }
    try {
      // What the export wrote, so only what was TYPED carries: see ReadContext.
      const result = await readFinalizedReport(await f.arrayBuffer(), weekStart, { report });
      if (pick === pickRef.current) setRead(result);
    } catch (e) {
      if (pick !== pickRef.current) return;
      setError(e instanceof NotAWordFileError ? e.message : `Couldn’t read that file: ${e instanceof Error ? e.message : 'unknown error'}`);
    }
  };

  const warnings = read ? fileWarnings(read, kind, weekStart) : [];
  const summary = read ? carrySummary(read.carry) : [];
  const ready = !!file && !!read && !busy && (warnings.length === 0 || anyway) && (!startWarning || startOk);

  const submit = async () => {
    if (!file || !read || busyRef.current) return;
    busyRef.current = true;
    setError(null);
    let inSharePoint = !!uploaded;
    try {
      // ⚠️ The token first, inside the click: it may open the sign-in popup,
      // which browsers allow only while the click still counts as one.
      setBusy('Signing in to SharePoint…');
      const token = await getToken();
      let done = uploaded;
      if (!done) {
        setBusy('Uploading to SharePoint…');
        done = await uploadToJobFolder({
          getToken: async () => token,
          jobFolderUrl,
          subfolder: REPORTS_SUBFOLDER,
          // Named like the export, so the folder reads week by week. A second
          // upload of the same week keeps both files (SharePoint adds " 1").
          fileName: reportFilename({ ...report, version: kind }),
          file,
        });
        setUploaded(done);
        inSharePoint = true;
      }
      setBusy('Recording it in the Project Tracker…');
      await weeklyReportApi.recordUpload(projectId, {
        week: weekStart, kind, webUrl: done.webUrl, fileName: done.name, carryForward: read.carry,
        // The whole report, for the tab's "After upload" view: the internal copy's only.
        ...(kind === 'internal' ? { filed: read.filed } : {}),
      });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['weekly-uploads', projectId] }),
        // Next week's report carries from this one.
        qc.invalidateQueries({ queryKey: ['weekly-report', projectId] }),
      ]);
      onDone(`${KIND_LABEL[kind]} report uploaded for ${theWeekOf(weekStart)}.`);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'unknown error';
      // A 4xx is the Project Tracker deciding, not a dropped connection: asking
      // again gets the same answer, so "click Upload again" would be a loop.
      const status = statusOf(e);
      const refused = status !== undefined && status >= 400 && status < 500 && status !== 408 && status !== 429;
      setError(e instanceof GraphConsentError
        ? 'SharePoint access isn’t available yet (sign-in declined, or admin consent pending). Try again once it is.'
        : inSharePoint && refused
          ? `The file is in SharePoint (“${REPORTS_SUBFOLDER}”), but the Project Tracker didn’t accept it for this week: ${msg}`
          : inSharePoint
            ? `The file is in SharePoint, but the Project Tracker couldn’t record it: ${msg} Click Upload again to record it; the same file is used, not a second copy.`
            : `Upload failed: ${msg}`);
    } finally {
      busyRef.current = false;
      setBusy(null);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? () => undefined : onClose}
      closeOnBackdropClick={!busy}
      title={`Upload the finalized ${kind} report`}
      maxWidth={560}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={!!busy}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={!ready} isLoading={!!busy}>Upload</Button>
        </>
      )}
    >
      <p style={{ marginTop: 0 }}>
        The {kind} report for {theWeekOf(weekStart)}, as it was sent. It goes to the job&rsquo;s
        SharePoint folder (&ldquo;{REPORTS_SUBFOLDER}&rdquo;)
        {kind === 'internal' ? ', its whole text shows on this tab for the week,' : ','} and what you typed in it starts next week&rsquo;s.
      </p>
      <input
        type="file"
        aria-label="Finalized report (.docx)"
        accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
        disabled={!!busy}
        onChange={(e) => void choose(e.target.files?.[0])}
      />
      {read && (
        <div style={{ marginTop: 10 }}>
          {kind === 'internal' && <div>Read the whole report: {filedSummary(read.filed)}.</div>}
          {summary.length ? (
            <>
              <div>Read from the file, for next week:</div>
              <ul className="wr-up-list">{summary.map((s) => <li key={s}>{s}</li>)}</ul>
            </>
          ) : (
            <div className="wr-muted">
              Nothing typed was found to carry into next week (the section headings may have been renamed). It can still be uploaded.
            </div>
          )}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="wr-up-warn">
          {warnings.map((w) => <div key={w}>{w}</div>)}
          <label className="wr-up-check">
            <input type="checkbox" checked={anyway} onChange={(e) => setAnyway(e.target.checked)} disabled={!!busy} />
            <span>It&rsquo;s the right file. Upload it as the {kind} report for {theWeekOf(weekStart)}.</span>
          </label>
        </div>
      )}
      {startWarning && <StartWarning text={startWarning} checked={startOk} onChange={setStartOk} disabled={!!busy} />}
      {busy && <div className="wr-final-note" role="status">{busy}</div>}
      {error && <div className="wr-final-err" role="alert">{error}</div>}
    </Modal>
  );
}

/** The chain-start warning (chainStartWarning), with the tick it needs before going ahead. */
function StartWarning({ text, checked, onChange, disabled }: {
  text: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled: boolean;
}) {
  return (
    <div className="wr-up-warn">
      <div>{text}</div>
      <label className="wr-up-check">
        <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
        <span>Start this job&rsquo;s reports with this week.</span>
      </label>
    </div>
  );
}

// ─── "No report" ─────────────────────────────────────────────────────────────

function NoReportModal({ projectId, weekStart, startWarning, onClose, onDone }: {
  projectId: number;
  weekStart: string;
  /** Set when this mark would start the job's chain on an old week: see chainStartWarning. */
  startWarning: string | null;
  onClose: () => void;
  onDone: (message: string) => void;
}) {
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startOk, setStartOk] = useState(false);
  const ready = !!reason.trim() && !busy && (!startWarning || startOk);

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    setError(null);
    try {
      await weeklyReportApi.markNoReport(projectId, weekStart, reason.trim());
      await qc.invalidateQueries({ queryKey: ['weekly-uploads', projectId] });
      onDone(`${weekOf(weekStart)} is marked "No report".`);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Couldn’t mark the week.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={busy ? () => undefined : onClose}
      title={`No report for ${theWeekOf(weekStart)}`}
      maxWidth={520}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={!ready} isLoading={busy}>Mark the week</Button>
        </>
      )}
    >
      <p style={{ marginTop: 0 }}>
        For a week with no report to send, such as a demobilized or shutdown week. It counts as filed, so next week&rsquo;s
        report can be uploaded, and the reason shows on the week.
      </p>
      <textarea
        className="wr-up-reason"
        aria-label="Why there is no report"
        placeholder="e.g. Demobilized until the 20th"
        maxLength={1000}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
      />
      {startWarning && <StartWarning text={startWarning} checked={startOk} onChange={setStartOk} disabled={busy} />}
      {error && <div className="wr-final-err" role="alert">{error}</div>}
    </Modal>
  );
}
